import { StateCreator } from 'zustand'
import { mercuryApi } from '$api'
import { SharedSlice } from '../../../../slice'
import { POSStaffMember } from './_common/interfaces/posStaffMember'
import {
  Calendar,
  EarningsRate,
  PayClassification,
  StaffMemberDetail,
  StaffRole,
  SuperFund,
} from '$models'
import { PermissionType, exceptionMessageHandler } from '$utils'
import useAppStore from '$appStore'
import { AuthModule, PayrollSystem } from '$constants'
import {
  staffMemberService,
  payrollService,
  payClassificationService,
  staffRoleService,
} from '$services'

interface StaffMembersState {
  staffMembers: StaffMemberDetail[]
  showDeactivated: boolean
  posStaffMembers: POSStaffMember[]
  staffRoles: StaffRole[]
  payClassifications: PayClassification[]
  calendars: Calendar[]
  earningsRates: EarningsRate[]
  superFunds: SuperFund[]
  payrollSuperFunds: SuperFund[]
}
export interface StaffMembersSlice extends StaffMembersState {
  setShowDeactivated: () => void

  /**
   * Get Staff Members list
   * @param includeDeleted
   */
  getStaffMembers: (includeDeleted?: boolean) => Promise<void>

  /**
   * Get Staff Roles
   * @param includeDeleted
   */
  getStaffRoles: (includeDeleted: boolean) => Promise<void>

  /**
   * Get POS Staff Members
   * @param includeDeleted
   */
  getPosStaff: (includeDeleted: boolean) => Promise<void>

  /**
   * Get Pay Classifications list
   */
  getPayClassifications: () => Promise<void>

  /**
   * Get Pay Calendars list
   * @param payrollSystem company payroll system
   */
  getCalendars: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Get Payroll Ordinary Earnings Rates list
   * @param payrollSystem company payroll system
   */
  getEarningsRates: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Get Payroll SuperFunds list
   * @param payrollSystem company payroll system
   */
  getPayrollSuperFunds: (payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Get SuperFunds list
   */
  getSuperFunds: () => Promise<void>

  /**
   * Check Third Party Connection Status
   * @param authModule AuthModule to check
   */
  checkThirdPartyConnectionStatus: (authModule: AuthModule) => Promise<boolean>

  /**
   * Resets StaffMembers Slice state
   */
  resetStaffMembersSlice: () => void
}

const initialState: StaffMembersState = {
  staffMembers: [],
  posStaffMembers: [],
  showDeactivated: false,
  staffRoles: [],
  payClassifications: [],
  calendars: [],
  earningsRates: [],
  superFunds: [],
  payrollSuperFunds: [],
}

const createStaffMembersSlice: StateCreator<
  StaffMembersSlice & SharedSlice,
  [],
  [],
  StaffMembersSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  setShowDeactivated: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),
  resetStaffMembersSlice: () => set(initialState),

  // State API calls
  getPosStaff: async (includeDeleted: boolean): Promise<void> => {
    const query = `includeDeleted=${includeDeleted}`
    return mercuryApi
      .get<POSStaffMember[]>(`/pos/staff?${query}`)
      .then((result) => set(() => ({ posStaffMembers: result })))
      .catch((error) => {
        set(() => ({
          posStaffMembers: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve Linked POS Staff Members'
        )
      })
  },

  getStaffRoles: async (includeDeleted: boolean): Promise<void> => {
    return staffRoleService
      .getAll(includeDeleted)
      .then((result) => set(() => ({ staffRoles: result })))
      .catch((error) => {
        set(() => ({
          staffRoles: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve Linked POS Staff Roles'
        )
      })
  },

  getStaffMembers: async (includeDeleted: boolean = true): Promise<void> => {
    //It defaults to today's date in order to get all the staff members until today
    return staffMemberService
      .getAll(undefined, includeDeleted)
      .then((result) => set(() => ({ staffMembers: result })))
      .catch((error) => {
        set(() => ({
          staffMembers: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  getPayClassifications: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission =
      activeCompany?.hasPermission(PermissionType.Payroll) ?? false
    const hasTimePermission =
      activeCompany?.hasPermission(PermissionType.Time) ?? false

    // if required permissions are missing initlize the pay classification to an empty array
    if (!(hasPayrollPermission && hasTimePermission)) {
      return set(() => ({ payClassifications: [] }))
    }

    return payClassificationService
      .getAll()
      .then((result) =>
        set({
          payClassifications: result,
        })
      )
      .catch((error) => {
        set(() => ({
          payClassifications: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve pay classifications')
      })
  },

  getCalendars: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getCalendars(payrollSystem)
      .then((result) => {
        set(() => ({
          calendars: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          calendars: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve third party calendars'
        )
      })
  },

  getEarningsRates: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getEarningsRates(payrollSystem)
      .then((result) => {
        set(() => ({
          earningsRates: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          earningsRates: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve third party earnings rates'
        )
      })
  },

  getPayrollSuperFunds: async (payrollSystem: PayrollSystem): Promise<void> => {
    return payrollService
      .getPayrollSuperFunds(payrollSystem)
      .then((result) => {
        set(() => ({
          payrollSuperFunds: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          payrollSuperFunds: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve third party super funds'
        )
      })
  },

  getSuperFunds: async (): Promise<void> => {
    return payrollService
      .getSuperFunds()
      .then((result) => {
        set(() => ({
          superFunds: result,
        }))
      })
      .catch((error) => {
        set(() => ({
          superFunds: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve super funds')
      })
  },

  checkThirdPartyConnectionStatus: async (
    authModule: AuthModule
  ): Promise<boolean> => {
    return payrollService
      .getThirdPartyConnectionStatus(authModule)
      .then((result) => {
        return result.hasAuth
      })
      .catch((error) => {
        exceptionMessageHandler(
          error,
          'Failed to check third party connection status'
        )
        return false
      })
  },
})

export default createStaffMembersSlice
